import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Flag, CheckCircle, Person, CalendarToday } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getPriorityColor, getStatusColor, getTaskTypeColor } from './calendarColorUtils';
import { COLOR_CODE_OPTIONS } from '../../../shared/appConstants';
import { COLORS } from '../../../shared/colors';
import { formatDateWithOptionalTime, isDueDateOverdue } from '../../../shared/timeUtils';
import './CalendarView.scss';

const lightenColor = (color, percent = 85) => {
  const num = parseInt(color.replace('#', ''), 16);
  const r = (num >> 16) + Math.round((255 - (num >> 16)) * (percent / 100));
  const g = ((num >> 8) & 0x00FF) + Math.round((255 - ((num >> 8) & 0x00FF)) * (percent / 100));
  const b = (num & 0x0000FF) + Math.round((255 - (num & 0x0000FF)) * (percent / 100));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

const CalendarTask = ({ task, onClick, colorCodeOption }) => {
  const { t } = useTranslation();
  const getDisplayValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.name || value.title || '';
  };

  // Check if task is overdue
  const isOverdue = useMemo(() => {
    return isDueDateOverdue(task.dueDateRaw || task.dueDate, task.hasDueTime, task.completed);
  }, [task.dueDate, task.dueDateRaw, task.hasDueTime, task.completed]);

  const dueDateLabel = formatDateWithOptionalTime(task.dueDateRaw || task.dueDate, task.hasDueTime);
  const dueTimeLabel = useMemo(() => {
    if (!task.hasDueTime || !(task.dueDateRaw || task.dueDate)) return '';
    const date = new Date(task.dueDateRaw || task.dueDate);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }, [task.dueDate, task.dueDateRaw, task.hasDueTime]);

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontSize: '13px', wordBreak: 'break-all' }}>
        {task.ticketNumber && `${task.ticketNumber}: `}{task.title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Flag sx={{ fontSize: 14, color: getPriorityColor(task.priority) }} />
        <Typography variant="caption" sx={{ fontSize: '11px', wordBreak: 'break-all' }}><strong>{t('calendar.priority')}</strong> {getDisplayValue(task.priority)}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <CheckCircle sx={{ fontSize: 14, color: getStatusColor(task.status) }} />
        <Typography variant="caption" sx={{ fontSize: '11px', wordBreak: 'break-all' }}><strong>{t('calendar.status')}</strong> {getDisplayValue(task.status)}</Typography>
      </Box>
      {(task.assignees?.length > 0 || task.assignee) && (() => {
        const names = task.assignees?.length > 0
          ? task.assignees.map(a => typeof a === 'object' ? a.name : a)
          : [task.assignee];
        return (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
            <Person sx={{ fontSize: 14, color: COLORS.white, mt: '1px', flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 700 }}>{t('calendar.assignee')}</Typography>
              {names.map((name, i) => (
                <Typography key={i} variant="caption" sx={{ fontSize: '11px', display: 'block' }}>• {name}</Typography>
              ))}
            </Box>
          </Box>
        );
      })()}
      {(task.dueDateRaw || task.dueDate) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <CalendarToday sx={{ fontSize: 14, color: COLORS.white }} />
          <Typography variant="caption" sx={{ fontSize: '11px', whiteSpace: 'nowrap' }}><strong>{t('calendar.dueDate')}</strong> {dueDateLabel}</Typography>
        </Box>
      )}
    </Box>
  );

  const getTaskColors = () => {
    if (colorCodeOption === COLOR_CODE_OPTIONS.STATUS) {
      const borderColor = getStatusColor(task.status);
      const bgColor = lightenColor(borderColor);
      return { bgColor, borderColor };
    }
    if (colorCodeOption === COLOR_CODE_OPTIONS.PRIORITY) {
      const borderColor = getPriorityColor(task.priority);
      const bgColor = lightenColor(borderColor);
      return { bgColor, borderColor };
    }
    if (colorCodeOption === COLOR_CODE_OPTIONS.TASK_TYPE) {
      const borderColor = getTaskTypeColor(task.taskType);
      const bgColor = lightenColor(borderColor);
      return { bgColor, borderColor };
    }
    return { bgColor: COLORS.calendarTask.defaultBackground, borderColor: COLORS.calendarTask.defaultBorder };
  };

  const { bgColor, borderColor } = getTaskColors();

  return (
    <Tooltip 
      title={tooltipContent} 
      arrow 
      placement="top"
      slotProps={{ 
        tooltip: { className: 'app-tooltip', sx: { maxWidth: 280 } },
        popper: { className: 'calendar-task-tooltip' }
      }}
    >
      <Box 
        className={`calendar-task ${isOverdue ? 'calendar-task-overdue' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        sx={{
          backgroundColor: bgColor,
          borderLeftColor: borderColor,
          '&:hover': {
            backgroundColor: lightenColor(borderColor, 75)
          }
        }}
      >
        <Typography className="calendar-task-title">
          {task.ticketNumber && `${task.ticketNumber}: `}{task.title}{dueTimeLabel ? ` • ${dueTimeLabel}` : ''}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default CalendarTask;
